import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../apiHelpers';
import Panel from '../../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import FadeIn from '../../components/ui/FadeIn';
import { CATEGORY } from '../../components/ui/categoryColors';

const stages = [
  { key: 'pending', label: 'Pending', color: 'bg-outline', text: 'text-on-surface-variant' },
  { key: 'confirmed', label: 'Confirmed', fill: CATEGORY.blue.mark.oklch, ink: CATEGORY.blue.ink.oklch },
  { key: 'shipped', label: 'Shipped', fill: CATEGORY.orange.mark.oklch, ink: CATEGORY.orange.ink.oklch },
  { key: 'complete', label: 'Complete', fill: CATEGORY.green.mark.oklch, ink: CATEGORY.green.ink.oklch },
];

const SalesPipelineModule = () => {
  const api = useApi();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await api.get('/orders', { silent: true });
        setOrders((data || []).filter(o => o.status !== 'cancelled'));
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <Panel title="Sales Pipeline" titleStyle="section" accent="blue" level={3}>
        <SkeletonGroup label="Loading sales pipeline" className="space-y-3">
          <Skeleton className="h-4 w-full rounded-full" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stages.map(st => <Skeleton key={st.key} className="h-12" />)}
          </div>
        </SkeletonGroup>
      </Panel>
    );
  }

  const counts = stages.map(s => ({
    ...s,
    count: orders.filter(o => o.status === s.key).length,
  }));
  const total = orders.length;

  return (
    <Panel
      title="Sales Pipeline"
      titleStyle="section"
      accent="blue"
      lit
      level={3}
      actions={<span className="text-xs text-on-surface-variant tabular-nums">{total} orders</span>}
    >
      <FadeIn>
      {/* Funnel bar */}
      {total > 0 && (
        <div className="flex rounded-full h-4 overflow-hidden mb-4 bg-gray-900/60">
          {counts.map(stage => (
            stage.count > 0 && (
              <div
                key={stage.key}
                className={`${stage.color || ''} transition-all duration-700 flex items-center justify-center`}
                style={{ background: stage.fill, width: `${(stage.count / total) * 100}%`, minWidth: stage.count > 0 ? '16px' : '0' }}
              >
                <span className="text-[9px] font-bold text-white/90">{stage.count}</span>
              </div>
            )
          ))}
        </div>
      )}

      {/* Stage counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {counts.map(stage => (
          <Link key={stage.key} to={`/orders`}
            className="text-center p-2 rounded-md hover:bg-gray-700/20 transition-colors">
            <div className={`text-xl font-bold tabular-nums ${stage.text || ''}`} style={stage.ink ? { color: stage.ink } : undefined}>{stage.count}</div>
            <div className="text-[10px] text-on-surface-variant uppercase tracking-wider">{stage.label}</div>
          </Link>
        ))}
      </div>
      </FadeIn>
    </Panel>
  );
};

export default SalesPipelineModule;
